import { Alert, Button, Checkbox, Field, Modal } from '@catering/ui';
import type { JSX } from 'react';
import { useState } from 'react';

import { describeOrderError } from '../orders/error-messages';
import type { OrderTemplateItem, OrderTemplatePosition } from './api';
import { useMenuPositions } from './use-menu-positions';

export interface TemplateFormModalSite {
  id: string;
  name: string;
}

export interface TemplateFormModalOperatingUnit {
  id: string;
  name: string;
}

export interface TemplateFormModalValues {
  siteId: string;
  /** Empty until chosen; only meaningful on create with several units. */
  operatingUnitId?: string;
  name: string;
  daysOfWeek: number[];
  isActive: boolean;
  items: OrderTemplateItem[];
}

export interface TemplateFormModalProps {
  mode: 'create' | 'edit';
  sites: TemplateFormModalSite[];
  /** The selector shows only on create and with more than one unit. */
  operatingUnits?: TemplateFormModalOperatingUnit[];
  defaultValues: TemplateFormModalValues;
  submitting: boolean;
  error: unknown;
  onSubmit: (values: TemplateFormModalValues) => void | Promise<void>;
  onClose: () => void;
}

const DAY_LABELS: Record<number, string> = {
  1: 'Lun',
  2: 'Mar',
  3: 'Mié',
  4: 'Jue',
  5: 'Vie',
  6: 'Sáb',
  7: 'Dom',
};

interface PositionOption {
  positionId: string;
  label: string;
}

/**
 * `rotating-menu/tasks.md` PR 11 gap: templates are day-independent
 * (position + quantity), so the picker reads `GET /portal/menu-positions`
 * (every ACTIVE position, ordered) instead of today's calendar — a day
 * without a published menu no longer blocks building a template. Ordered
 * by `displayOrder`, which already groups a position's own service type
 * together (`design.md` §3.5: the endpoint returns `serviceTypeId` only,
 * not the service type's name, so no group header label is shown here).
 * A `positionId` already on the template but missing from the active list
 * (deactivated since) still gets a synthetic option labeled "Posición inactiva",
 * so an existing line never silently loses its selection.
 */
function buildPositionOptions(
  positions: OrderTemplatePosition[],
  items: OrderTemplateItem[],
): PositionOption[] {
  const byPositionId = new Map<string, PositionOption>();

  for (const position of [...positions].sort((a, b) => a.displayOrder - b.displayOrder)) {
    byPositionId.set(position.id, { positionId: position.id, label: position.name });
  }

  for (const item of items) {
    if (item.positionId !== '' && !byPositionId.has(item.positionId)) {
      byPositionId.set(item.positionId, {
        positionId: item.positionId,
        label: 'Posición inactiva',
      });
    }
  }

  return Array.from(byPositionId.values());
}

/**
 * `portal-templates/spec.md` → "ABM de plantillas propias". No customer
 * select (`design.md` §0.13: siempre `scope.customers[0]`), no "Generar
 * pedidos ahora" (`portal-templates/spec.md` → "Namespace portal no
 * expone la generación explícita de pedidos").
 */
export function TemplateFormModal({
  mode,
  sites,
  operatingUnits = [],
  defaultValues,
  submitting,
  error,
  onSubmit,
  onClose,
}: TemplateFormModalProps): JSX.Element {
  const [siteId, setSiteId] = useState(defaultValues.siteId || sites[0]?.id || '');
  const hasUnitChoice = mode === 'create' && operatingUnits.length > 1;
  const [operatingUnitId, setOperatingUnitId] = useState(defaultValues.operatingUnitId ?? '');
  const [missingUnit, setMissingUnit] = useState(false);
  const [formProblem, setFormProblem] = useState<string | null>(null);
  const [name, setName] = useState(defaultValues.name);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>(defaultValues.daysOfWeek);
  const [isActive, setIsActive] = useState(defaultValues.isActive);
  const [items, setItems] = useState<OrderTemplateItem[]>(defaultValues.items);

  // Positions are those of the unit's contract: the chosen one on create, the template's own on edit.
  const positionsUnitId = hasUnitChoice
    ? operatingUnitId
    : operatingUnits.length > 1
      ? defaultValues.operatingUnitId
      : undefined;
  const menuPositions = useMenuPositions(
    positionsUnitId || undefined,
    !hasUnitChoice || operatingUnitId !== '',
  );
  const positions = Array.isArray(menuPositions.data) ? menuPositions.data : [];
  const positionOptions = buildPositionOptions(positions, items);
  const errorMessage = error === undefined || error === null ? '' : describeOrderError(error);

  function toggleDay(day: number): void {
    setDaysOfWeek((current) =>
      current.includes(day) ? current.filter((value) => value !== day) : [...current, day],
    );
  }

  function updateItem(index: number, patch: Partial<OrderTemplateItem>): void {
    setItems((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }

  function removeItem(index: number): void {
    setItems((current) => current.filter((_, i) => i !== index));
  }

  function addItem(): void {
    setItems((current) => [...current, { positionId: '', quantity: 1, notes: null }]);
  }

  function handleSubmit(): void {
    if (hasUnitChoice && operatingUnitId === '') {
      setMissingUnit(true);

      return;
    }

    setMissingUnit(false);

    const problem =
      name.trim() === ''
        ? 'Poné un nombre para la plantilla.'
        : daysOfWeek.length === 0
          ? 'Elegí al menos un día.'
          : items.length === 0
            ? 'Agregá al menos un plato.'
            : items.some((item) => item.positionId === '' || !(item.quantity >= 1))
              ? 'Elegí un plato y una cantidad en cada línea.'
              : null;

    setFormProblem(problem);

    if (problem !== null) {
      return;
    }

    void onSubmit({
      siteId,
      ...(hasUnitChoice ? { operatingUnitId } : {}),
      name,
      daysOfWeek,
      isActive,
      items,
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={mode === 'create' ? 'Nueva plantilla' : 'Editar plantilla'}
      className="max-w-2xl"
    >
      <div className="flex flex-col gap-6">
        {errorMessage !== '' ? <Alert tone="danger">{errorMessage}</Alert> : null}
        {formProblem !== null ? <Alert tone="danger">{formProblem}</Alert> : null}

        <Field
          label="Nombre"
          name="name"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
        />

        {missingUnit ? <Alert tone="danger">Elegí un punto de venta para la plantilla.</Alert> : null}

        {hasUnitChoice ? (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="template-operating-unit" className="text-sm font-medium text-slate-900">
              Punto de venta
            </label>
            <select
              id="template-operating-unit"
              value={operatingUnitId}
              onChange={(event) => setOperatingUnitId(event.target.value)}
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            >
              <option value="" disabled>
                Elegí un punto de venta
              </option>
              {operatingUnits.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="template-site" className="text-sm font-medium text-slate-900">
            Sede
          </label>
          <select
            id="template-site"
            value={siteId}
            onChange={(event) => setSiteId(event.target.value)}
            className="h-11 rounded-md border border-slate-300 px-3 text-sm"
          >
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
        </div>

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-slate-900">Días de la semana</legend>
          <div className="flex flex-wrap gap-3">
            {[1, 2, 3, 4, 5, 6, 7].map((day) => (
              <Checkbox
                key={day}
                label={DAY_LABELS[day] ?? String(day)}
                checked={daysOfWeek.includes(day)}
                onChange={() => toggleDay(day)}
              />
            ))}
          </div>
        </fieldset>

        <div className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-slate-900">Líneas de la plantilla</h2>
          {items.map((item, index) => (
            <div
              key={index}
              className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr_auto] sm:items-end"
            >
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor={`template-item-${index}`}
                  className="text-sm font-medium text-slate-900"
                >
                  Posición
                </label>
                <select
                  id={`template-item-${index}`}
                  value={item.positionId}
                  onChange={(event) => updateItem(index, { positionId: event.target.value })}
                  className="h-11 rounded-md border border-slate-300 px-3 text-sm"
                >
                  <option value="">Seleccioná una posición</option>
                  {positionOptions.map((option) => (
                    <option key={option.positionId} value={option.positionId}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <Field
                label="Cantidad"
                name={`items.${index}.quantity`}
                type="number"
                min={1}
                value={item.quantity}
                onChange={(event) => updateItem(index, { quantity: Number(event.target.value) })}
              />
              <Button type="button" variant="secondary" onClick={() => removeItem(index)}>
                Quitar
              </Button>
            </div>
          ))}
          <div>
            <Button type="button" variant="secondary" onClick={addItem}>
              Agregar línea
            </Button>
          </div>
        </div>

        {mode === 'edit' ? (
          <Checkbox
            label="Plantilla activa"
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
        ) : null}

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button loading={submitting} onClick={handleSubmit}>
            Guardar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
